/**
 * A critically-damped spring. Given a target it moves smoothly with no
 * overshoot and no discontinuity in velocity — which is exactly what a camera
 * needs: it can be re-aimed every frame and still never jerks.
 */
export class Spring {
  private readonly stiffness: number;
  private value: number;
  private velocity = 0;

  constructor(responseSeconds: number, initial = 0) {
    // A critically-damped spring settles in roughly 4/omega seconds.
    this.stiffness = 4 / Math.max(0.001, responseSeconds);
    this.value = initial;
  }

  public update(target: number, deltaSeconds: number): number {
    if (!Number.isFinite(this.value) || !Number.isFinite(this.velocity)) {
      // A spring that has gone non-finite would stay that way forever, and this
      // one is driving a camera. Snap to the target and carry on.
      this.reset(Number.isFinite(target) ? target : 0);
    }
    // Sub-stepping keeps the integration stable when a frame runs long.
    const steps = Math.min(4, Math.max(1, Math.ceil(deltaSeconds * 60)));
    const step = deltaSeconds / steps;
    for (let i = 0; i < steps; i += 1) {
      const acceleration =
        this.stiffness * this.stiffness * (target - this.value) -
        2 * this.stiffness * this.velocity;
      this.velocity += acceleration * step;
      this.value += this.velocity * step;
    }
    return this.value;
  }

  public get current(): number {
    return this.value;
  }

  public reset(value: number): void {
    this.value = value;
    this.velocity = 0;
  }
}
